import type {
  MomentCarouselItem,
  MomentCommerceIntro,
  MomentImageAsset,
  MomentListItem,
  MomentMessageType,
} from "../data/momentOptions";
import { uploadMomentImage } from "../api/momentApi";
import { createMomentImageName } from "./messageBodyBuilder";

export type UploadedImages = {
  wideImage: MomentImageAsset;
  listItems: MomentListItem[];
  carouselItems: MomentCarouselItem[];
  commerceIntro: MomentCommerceIntro;
};

/**
 * 저장 시점에 아직 업로드되지 않은(imageFile만 있고 imageUploadedUrl이 없는) 이미지를
 * S3에 순차 업로드하고 uploadedUrl을 채워 반환한다.
 */
export async function uploadPendingImages(
  messageType: MomentMessageType,
  wideImage: MomentImageAsset,
  listItems: MomentListItem[],
  carouselItems: MomentCarouselItem[],
  commerceIntro: MomentCommerceIntro,
  commerceIntroEnabled: boolean,
  timestamp: string,
  setWideImage: (v: MomentImageAsset) => void,
  setListItems: (v: MomentListItem[]) => void,
  setCarouselItems: (v: MomentCarouselItem[]) => void,
  setCommerceIntro: (v: MomentCommerceIntro) => void
): Promise<UploadedImages> {
  if (messageType === "BASIC_TEXT_MESSAGE") {
    return { wideImage, listItems, carouselItems, commerceIntro };
  }

  const uploadAsset = async (
    asset: { imageFile: File | null; imageUploadedUrl: string },
    imageName: string
  ): Promise<string> => {
    if (asset.imageUploadedUrl) return asset.imageUploadedUrl;
    if (!asset.imageFile) throw new Error("업로드할 이미지 파일이 없습니다.");
    const uploaded = await uploadMomentImage(asset.imageFile, imageName);
    return uploaded.imgUrl;
  };

  let seq = 1;

  // ── WIDE_MESSAGE: 단일 이미지, 순차 업로드
  let nextWideImage = wideImage;
  if (messageType === "WIDE_MESSAGE" && (wideImage.imageFile || wideImage.imageUploadedUrl)) {
    const uploadedUrl = await uploadAsset(wideImage, createMomentImageName(timestamp, seq));
    seq += 1;
    nextWideImage = { ...wideImage, imageUploadedUrl: uploadedUrl };
    setWideImage(nextWideImage);
  }

  // ── WIDE_LIST_MESSAGE: 3~5개 이미지, 순차 업로드
  let nextListItems = listItems;
  if (messageType === "WIDE_LIST_MESSAGE") {
    const uploadedItems: MomentListItem[] = [];
    for (const item of listItems) {
      if (!item.imageFile && !item.imageUploadedUrl) {
        uploadedItems.push(item);
        continue;
      }
      const uploadedUrl = await uploadAsset(item, createMomentImageName(timestamp, seq));
      uploadedItems.push({ ...item, imageUploadedUrl: uploadedUrl });
      seq += 1;
    }
    nextListItems = uploadedItems;
    setListItems(nextListItems);
  }

  // ── CAROUSEL: 인트로(선택) + 카드 1~6개
  let nextCarouselItems = carouselItems;
  let nextCommerceIntro = commerceIntro;
  if (messageType === "CAROUSEL_FEED_MESSAGE" || messageType === "CAROUSEL_COMMERCE_MESSAGE") {
    // 인트로 카드 (CAROUSEL_COMMERCE_MESSAGE + commerceIntroEnabled 일 때만)
    if (
      messageType === "CAROUSEL_COMMERCE_MESSAGE" &&
      commerceIntroEnabled &&
      (commerceIntro.imageFile || commerceIntro.imageUploadedUrl)
    ) {
      const uploadedUrl = await uploadAsset(commerceIntro, createMomentImageName(timestamp, seq));
      seq += 1;
      nextCommerceIntro = { ...commerceIntro, imageUploadedUrl: uploadedUrl };
      setCommerceIntro(nextCommerceIntro);
    }

    const uploadedItems: MomentCarouselItem[] = [];
    for (const item of carouselItems) {
      if (!item.imageFile && !item.imageUploadedUrl) {
        uploadedItems.push(item);
        continue;
      }
      const uploadedUrl = await uploadAsset(item, createMomentImageName(timestamp, seq));
      uploadedItems.push({ ...item, imageUploadedUrl: uploadedUrl });
      seq += 1;
    }
    nextCarouselItems = uploadedItems;
    setCarouselItems(nextCarouselItems);
  }

  return {
    wideImage: nextWideImage,
    listItems: nextListItems,
    carouselItems: nextCarouselItems,
    commerceIntro: nextCommerceIntro,
  };
}
